import React from "react";
import { Form, InputGroup } from "react-bootstrap";

// Server findings and client checks for one path: red for an error, amber for a warning.
export const Messages = ({ id, messages }) => {
  if (!messages || messages.length === 0) {
    return null;
  }
  return (
    <div id={id} role={messages.some((m) => m.severity === "error") ? "alert" : undefined}>
      {messages.map((item, index) => (
        <span
          key={`${item.severity}-${index}-${item.message}`}
          className={`warranty-field-msg is-${item.severity}`}
        >
          {item.severity === "warning" ? "Warning: " : ""}
          {item.message}
        </span>
      ))}
    </div>
  );
};

const hasError = (messages) => (messages || []).some((item) => item.severity === "error");

const NUMBER_FILTERS = {
  rupees: /^\d{0,12}(\.\d{0,2})?$/,
  percent: /^\d{0,3}(\.\d{0,2})?$/,
  whole: /^\d{0,9}$/,
};

export const TextField = ({
  id,
  label,
  value,
  onChange,
  messages,
  placeholder,
  maxLength,
  disabled,
  help,
  type = "text",
}) => (
  <Form.Group className="mb-3">
    <Form.Label htmlFor={id}>{label}</Form.Label>
    <Form.Control
      id={id}
      type={type}
      value={value}
      maxLength={maxLength}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={hasError(messages)}
      aria-describedby={messages && messages.length > 0 ? `${id}-msg` : undefined}
      onChange={(event) => onChange(event.target.value)}
    />
    {help && <div className="warranty-help">{help}</div>}
    <Messages id={`${id}-msg`} messages={messages} />
  </Form.Group>
);

// kind: "rupees" | "percent" | "whole". Keystrokes that cannot be part of such a number are ignored,
// so the form only ever holds text the model can read (or a blank).
export const NumberField = ({
  id,
  label,
  kind,
  value,
  onChange,
  messages,
  disabled,
  help,
  unit,
  placeholder,
}) => (
  <Form.Group className="mb-3">
    <Form.Label htmlFor={id}>{label}</Form.Label>
    <InputGroup>
      {kind === "rupees" && <InputGroup.Text>₹</InputGroup.Text>}
      <Form.Control
        id={id}
        type="text"
        inputMode={kind === "whole" ? "numeric" : "decimal"}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={hasError(messages)}
        aria-describedby={messages && messages.length > 0 ? `${id}-msg` : undefined}
        onChange={(event) => {
          if (NUMBER_FILTERS[kind].test(event.target.value)) {
            onChange(event.target.value);
          }
        }}
      />
      {kind === "percent" && <InputGroup.Text>%</InputGroup.Text>}
      {unit && <InputGroup.Text>{unit}</InputGroup.Text>}
    </InputGroup>
    {help && <div className="warranty-help">{help}</div>}
    <Messages id={`${id}-msg`} messages={messages} />
  </Form.Group>
);

export const SelectField = ({ id, label, value, onChange, options, messages, disabled, help }) => (
  <Form.Group className="mb-3">
    <Form.Label htmlFor={id}>{label}</Form.Label>
    <select
      id={id}
      className="form-control"
      value={value}
      disabled={disabled}
      aria-invalid={hasError(messages)}
      onChange={(event) => onChange(event.target.value)}
    >
      {!options.some((option) => option.value === value) && (
        <option value={value}>{value === "" ? "Choose" : value}</option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {help && <div className="warranty-help">{help}</div>}
    <Messages id={`${id}-msg`} messages={messages} />
  </Form.Group>
);

// Multi-line text. `rows` sets the starting height; the person can drag it taller.
export const TextAreaField = ({ id, label, value, onChange, messages, placeholder, maxLength, disabled, help, rows = 5 }) => (
  <Form.Group className="mb-3">
    <Form.Label htmlFor={id}>{label}</Form.Label>
    <Form.Control
      as="textarea"
      id={id}
      rows={rows}
      value={value}
      maxLength={maxLength}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={hasError(messages)}
      aria-describedby={messages && messages.length > 0 ? `${id}-msg` : undefined}
      onChange={(event) => onChange(event.target.value)}
    />
    {help && <div className="warranty-help">{help}</div>}
    <Messages id={`${id}-msg`} messages={messages} />
  </Form.Group>
);

export const CheckField = ({ id, label, checked, onChange, disabled, help, messages }) => (
  <Form.Group className="mb-3 warranty-check">
    <Form.Check
      type="checkbox"
      id={id}
      label={label}
      checked={checked}
      disabled={disabled}
      aria-invalid={hasError(messages)}
      aria-describedby={messages && messages.length > 0 ? `${id}-msg` : undefined}
      onChange={(event) => onChange(event.target.checked)}
    />
    {help && <div className="warranty-help">{help}</div>}
    <Messages id={`${id}-msg`} messages={messages} />
  </Form.Group>
);

export const Section = ({ id, title, description, messages, children }) => (
  <section className="warranty-section" id={`warranty-sec-${id}`} aria-labelledby={`warranty-sec-${id}-title`}>
    <h5 id={`warranty-sec-${id}-title`}>{title}</h5>
    {description && <p className="warranty-help">{description}</p>}
    <Messages messages={messages} />
    {children}
  </section>
);
